import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  CalendarDays, Users, DollarSign, Clock,
  CheckCircle2, XCircle, Loader2, LogOut,
  Phone, Mail, Car, ChevronDown, Bell, BellRing, Volume2, VolumeX, Trash2, CalendarPlus,
  Star, MessageSquare, ThumbsUp, ThumbsDown,
  Pencil, Save, X, StickyNote, FileSpreadsheet, Printer, TrendingUp, BarChart3,
  CalendarOff, Settings, ToggleLeft, ToggleRight, ChevronLeft, ChevronRight, RefreshCw,
  ClipboardList, AlertTriangle, ChevronUp, ExternalLink
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 15000; // 15 seconds

const downloadBookingICS = (b) => {
  const match = b.time.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return;
  let hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  if (match[3].toUpperCase() === 'PM' && hours !== 12) hours += 12;
  if (match[3].toUpperCase() === 'AM' && hours === 12) hours = 0;
  const [y, m, d] = b.date.split('-').map(Number);
  const start = new Date(y, m - 1, d, hours, minutes);
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  const fmt = (dt) => dt.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT',
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:${b.name} - ${b.service}${b.package ? ` (${b.package})` : ''}`,
    `DESCRIPTION:Customer: ${b.name}\\nPhone: ${b.phone}\\nService: ${b.service}${b.package ? `\\nPackage: ${b.package}` : ''}${b.tint_shade ? `\\nTint: ${b.tint_shade}` : ''}${b.vehicle_info ? `\\nVehicle: ${b.vehicle_info}` : ''}`,
    `LOCATION:17960 SW Alvord Lane, Beaverton, OR`,
    'END:VEVENT', 'END:VCALENDAR'
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `booking-${b.name.replace(/\s+/g, '-').toLowerCase()}.ics`;
  a.click();
};

const statusColors = {
  pending: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  confirmed: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  completed: 'bg-green-500/15 text-green-400 border-green-500/30',
  cancelled: 'bg-red-500/15 text-red-400 border-red-500/30',
};

function playAlertSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // Play two tones for a pleasant alert
    [660, 880].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.15 + 0.4);
      osc.start(ctx.currentTime + i * 0.15);
      osc.stop(ctx.currentTime + i * 0.15 + 0.4);
    });
  } catch (e) { /* silent fail */ }
}

const StatusBadge = ({ status }) => (
  <span data-testid={`status-badge-${status}`} className={`px-3 py-1 text-xs font-semibold rounded-full border ${statusColors[status] || statusColors.pending}`}>
    {status.charAt(0).toUpperCase() + status.slice(1)}
  </span>
);

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card className="bg-zinc-900/50 border-zinc-800">
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-gray-400 text-sm">{label}</p>
        <p className="text-white text-2xl font-bold">{value}</p>
      </div>
    </CardContent>
  </Card>
);

const BookingRow = ({ booking, onStatusChange, onDelete, isUpdating, isNew }) => {
  const [showActions, setShowActions] = useState(false);

  return (
    <div data-testid={`booking-row-${booking.id}`} className={`bg-zinc-900/40 border rounded-xl p-5 space-y-3 transition-all duration-500 ${isNew ? 'border-amber-500/60 ring-1 ring-amber-500/20' : 'border-zinc-800/50'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          {isNew && (
            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-black rounded-full animate-pulse">New</span>
          )}
          <div>
            <h4 className="text-white font-semibold text-lg">{booking.name}</h4>
            <p className="text-amber-500 font-medium">{booking.service}</p>
            {booking.package && (
              <p className="text-zinc-400 text-sm">{booking.package}</p>
            )}
          </div>
        </div>
        <StatusBadge status={booking.status} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div className="flex items-center gap-2 text-gray-300">
          <CalendarDays className="w-4 h-4 text-gray-500" />
          <span>{booking.date} at {booking.time}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-300">
          <Phone className="w-4 h-4 text-gray-500" />
          <span>{booking.phone}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-300 min-w-0">
          <Mail className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span className="truncate text-xs">{booking.email || '—'}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-300">
          <DollarSign className="w-4 h-4 text-gray-500" />
          <span>${booking.total_price || booking.amount_paid || 50} via {booking.payment_method}</span>
        </div>
      </div>

      {booking.vehicle_info && (
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Car className="w-4 h-4 text-gray-500" />
          <span>{booking.vehicle_info}</span>
        </div>
      )}

      {booking.tint_shade && (
        <p className="text-sm text-gray-400">Tint: {booking.tint_shade}</p>
      )}

      {booking.message && (
        <p className="text-sm text-gray-400 italic">"{booking.message}"</p>
      )}

      {/* Action Buttons — always visible, single row */}
      <div className="pt-3 border-t border-zinc-800/50 grid grid-cols-3 gap-2">
        {booking.status === 'pending' ? (
          <button
            data-testid={`confirm-booking-${booking.id}`}
            disabled={isUpdating}
            onClick={() => onStatusChange(booking.id, 'confirmed')}
            className="flex items-center justify-center gap-1 py-2.5 rounded-lg bg-amber-500 text-black font-bold text-xs hover:bg-amber-400 transition-colors disabled:opacity-50"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Confirm
          </button>
        ) : booking.status === 'confirmed' ? (
          <button
            data-testid={`complete-booking-${booking.id}`}
            disabled={isUpdating}
            onClick={() => onStatusChange(booking.id, 'completed')}
            className="flex items-center justify-center gap-1 py-2.5 rounded-lg bg-green-600 text-white font-bold text-xs hover:bg-green-500 transition-colors disabled:opacity-50"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Complete
          </button>
        ) : (
          <button
            data-testid={`toggle-actions-${booking.id}`}
            onClick={() => setShowActions(!showActions)}
            className="flex items-center justify-center gap-1 py-2.5 rounded-lg border border-zinc-700 text-zinc-400 text-xs hover:text-white transition-colors"
          >
            Status <ChevronDown className={`w-3 h-3 transition-transform ${showActions ? 'rotate-180' : ''}`} />
          </button>
        )}
        <button
          onClick={() => { if (window.confirm('Delete this booking permanently?')) onDelete(booking.id); }}
          data-testid={`delete-booking-${booking.id}`}
          className="flex items-center justify-center gap-1 py-2.5 rounded-lg bg-red-500/80 text-white font-bold text-xs hover:bg-red-500 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
        <button
          onClick={() => downloadBookingICS(booking)}
          data-testid={`add-to-calendar-${booking.id}`}
          className="flex items-center justify-center gap-1 py-2.5 rounded-lg border border-blue-500/40 text-blue-400 font-bold text-xs hover:bg-blue-500/10 transition-colors"
        >
          <CalendarPlus className="w-3.5 h-3.5" /> Calendar
        </button>
      </div>
      {showActions && (
        <div className="flex flex-wrap gap-2 mt-2">
          {['confirmed', 'completed', 'cancelled', 'pending'].filter(s => s !== booking.status).map(status => (
            <Button
              key={status}
              data-testid={`set-status-${status}-${booking.id}`}
              size="sm"
              disabled={isUpdating}
              onClick={() => onStatusChange(booking.id, status)}
              variant="outline"
              className={`text-xs border-zinc-700 hover:bg-zinc-800 ${
                status === 'confirmed' ? 'text-blue-400' :
                status === 'completed' ? 'text-green-400' :
                status === 'cancelled' ? 'text-red-400' : 'text-amber-400'
              }`}
            >
              {status === 'confirmed' && <CheckCircle2 className="w-3 h-3 mr-1" />}
              {status === 'completed' && <CheckCircle2 className="w-3 h-3 mr-1" />}
              {status === 'cancelled' && <XCircle className="w-3 h-3 mr-1" />}
              {status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};


const ReportsTab = ({ bookings, payments }) => {
  const [dateRange, setDateRange] = useState('all');

  // Deposit amounts matching backend DEPOSIT_AMOUNTS
  const DEPOSIT_MAP = {
    'Window Tinting': { default: 50, 'Front Windshield Only': 0, 'Front Two Windows': 0, 'Sedan & Coupe': 50, 'Trucks & SUV': 50 },
    'Vinyl Wrap': { default: 200 },
    'Starlight Headliner': { default: 200 },
    'Ambient Lighting': { default: 200 },
    'Remove Tint': { default: 100 },
  };
  const getDeposit = (service, pkg) => {
    const svc = DEPOSIT_MAP[service] || { default: 50 };
    if (pkg && svc[pkg] !== undefined) return svc[pkg];
    return svc.default || 50;
  };

  const filterByDate = (items, dateField) => {
    if (dateRange === 'all') return items;
    const now = new Date();
    const cutoff = new Date();
    if (dateRange === '7d') cutoff.setDate(now.getDate() - 7);
    if (dateRange === '30d') cutoff.setDate(now.getDate() - 30);
    if (dateRange === '90d') cutoff.setDate(now.getDate() - 90);
    return items.filter(item => {
      const d = new Date(item[dateField]);
      return d >= cutoff;
    });
  };

  const filteredBookings = filterByDate(bookings, 'created_at');
  const filteredPayments = filterByDate(payments, 'created_at');

  // Revenue by service using actual deposit amounts
  const revenueByService = {};
  filteredBookings.forEach(b => {
    const svc = b.service || 'Unknown';
    const deposit = getDeposit(svc, b.package);
    if (!revenueByService[svc]) revenueByService[svc] = { count: 0, revenue: 0 };
    revenueByService[svc].count += 1;
    revenueByService[svc].revenue += deposit;
  });

  // Payment stats
  const paidPayments = filteredPayments.filter(p => p.payment_status === 'paid');
  const totalCollected = paidPayments.reduce((s, p) => s + (p.amount || 0), 0);
  const pendingPayments = filteredPayments.filter(p => p.payment_status === 'pending');
  const totalPending = pendingPayments.reduce((s, p) => s + (p.amount || 0), 0);

  // Bookings by status
  const statusCounts = {};
  filteredBookings.forEach(b => {
    const st = b.status || 'pending';
    statusCounts[st] = (statusCounts[st] || 0) + 1;
  });

  // Service breakdown for payments
  const paymentsByService = {};
  filteredPayments.forEach(p => {
    const svc = p.service || 'Unknown';
    if (!paymentsByService[svc]) paymentsByService[svc] = { count: 0, total: 0, paid: 0 };
    paymentsByService[svc].count += 1;
    paymentsByService[svc].total += (p.amount || 0);
    if (p.payment_status === 'paid') paymentsByService[svc].paid += (p.amount || 0);
  });

  const exportToExcel = () => {
    const XLSX = require('xlsx');
    const wb = XLSX.utils.book_new();

    // Sheet 1: Revenue Summary
    const summaryData = [
      ['PDX Window Tint - Revenue Report'],
      ['Generated', new Date().toLocaleString()],
      ['Date Range', dateRange === 'all' ? 'All Time' : `Last ${dateRange}`],
      [],
      ['REVENUE SUMMARY'],
      ['Total Collected', `$${totalCollected.toFixed(2)}`],
      ['Total Pending', `$${totalPending.toFixed(2)}`],
      ['Total Bookings', filteredBookings.length],
      [],
      ['REVENUE BY SERVICE'],
      ['Service', 'Bookings', 'Deposit Revenue'],
      ...Object.entries(revenueByService).map(([svc, d]) => [svc, d.count, `$${d.revenue.toFixed(2)}`]),
      [],
      ['BOOKINGS BY STATUS'],
      ['Status', 'Count'],
      ...Object.entries(statusCounts).map(([st, c]) => [st.charAt(0).toUpperCase() + st.slice(1), c]),
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
    ws1['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

    // Sheet 2: All Bookings
    const bookingRows = filteredBookings.map(b => ({
      Name: b.name, Service: b.service, Package: b.package || '', Date: b.date, Time: b.time,
      Phone: b.phone, Email: b.email || '', Vehicle: b.vehicle_info || '',
      Status: b.status, Payment: b.payment_method || '', Deposit: `$${getDeposit(b.service, b.package)}`,
    }));
    if (bookingRows.length > 0) {
      const ws2 = XLSX.utils.json_to_sheet(bookingRows);
      ws2['!cols'] = Array(11).fill({ wch: 18 });
      XLSX.utils.book_append_sheet(wb, ws2, 'Bookings');
    }

    // Sheet 3: All Payments
    const paymentRows = filteredPayments.map(p => ({
      Customer: p.name || '', Service: p.service || '', Package: p.package || '',
      Amount: `$${p.amount || 0}`, Status: p.payment_status || 'pending',
      Notes: p.notes || '', Date: p.created_at?.slice(0, 16).replace('T', ' ') || '',
    }));
    if (paymentRows.length > 0) {
      const ws3 = XLSX.utils.json_to_sheet(paymentRows);
      ws3['!cols'] = Array(7).fill({ wch: 18 });
      XLSX.utils.book_append_sheet(wb, ws3, 'Payments');
    }

    XLSX.writeFile(wb, `PDX_Revenue_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Excel report downloaded');
  };

  const handlePrint = () => {
    const printContent = document.getElementById('reports-print-area');
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>PDX Window Tint - Revenue Report</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 30px; color: #111; }
        h1 { font-size: 22px; margin-bottom: 4px; }
        h2 { font-size: 16px; margin-top: 24px; border-bottom: 2px solid #f59e0b; padding-bottom: 4px; }
        .meta { color: #666; font-size: 12px; margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 13px; }
        th { background: #f5f5f5; text-align: left; padding: 8px; border: 1px solid #ddd; font-weight: 600; }
        td { padding: 8px; border: 1px solid #ddd; }
        .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 16px 0; }
        .stat-box { border: 1px solid #ddd; border-radius: 8px; padding: 12px; text-align: center; }
        .stat-box .num { font-size: 24px; font-weight: bold; color: #f59e0b; }
        .stat-box .label { font-size: 11px; color: #666; text-transform: uppercase; }
      </style></head><body>
      ${printContent.innerHTML}
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  const maxRevenue = Math.max(...Object.values(revenueByService).map(d => d.revenue), 1);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Revenue & Service Tracking</h2>
          <p className="text-zinc-500 text-xs mt-0.5">Track your business performance</p>
        </div>
        <div className="flex items-center gap-2">
          {['7d', '30d', '90d', 'all'].map(r => (
            <button
              key={r}
              data-testid={`report-range-${r}`}
              onClick={() => setDateRange(r)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                dateRange === r ? 'bg-amber-500 text-black' : 'bg-zinc-900 text-gray-400 hover:text-white'
              }`}
            >
              {r === 'all' ? 'All Time' : r === '7d' ? '7 Days' : r === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
          <button
            data-testid="export-excel-btn"
            onClick={exportToExcel}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg bg-green-600 hover:bg-green-500 text-white transition-colors"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
          </button>
          <button
            data-testid="print-report-btn"
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg bg-zinc-800 hover:bg-zinc-800 text-white transition-colors"
          >
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-zinc-900/40 border border-green-500/20 rounded-xl p-4 text-center">
          <p className="text-green-400 text-2xl font-bold">${totalCollected.toFixed(2)}</p>
          <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">Collected</p>
        </div>
        <div className="bg-zinc-900/40 border border-amber-500/20 rounded-xl p-4 text-center">
          <p className="text-amber-400 text-2xl font-bold">${totalPending.toFixed(2)}</p>
          <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">Pending</p>
        </div>
        <div className="bg-zinc-900/40 border border-blue-500/20 rounded-xl p-4 text-center">
          <p className="text-blue-400 text-2xl font-bold">{filteredBookings.length}</p>
          <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">Bookings</p>
        </div>
        <div className="bg-zinc-900/40 border border-purple-500/20 rounded-xl p-4 text-center">
          <p className="text-purple-400 text-2xl font-bold">{filteredPayments.length}</p>
          <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">Transactions</p>
        </div>
      </div>

      {/* Revenue by Service - Visual Bar Chart */}
      <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-4 h-4 text-amber-500" />
          <h3 className="text-white font-semibold text-sm">Revenue by Service</h3>
        </div>
        {Object.keys(revenueByService).length === 0 ? (
          <p className="text-zinc-600 text-sm text-center py-4">No data for this period</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(revenueByService).sort((a, b) => b[1].revenue - a[1].revenue).map(([svc, data]) => (
              <div key={svc}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-zinc-300 text-sm">{svc}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-zinc-500 text-xs">{data.count} bookings</span>
                    <span className="text-amber-400 text-sm font-bold">${data.revenue.toFixed(2)}</span>
                  </div>
                </div>
                <div className="w-full bg-zinc-950 rounded-full h-2.5">
                  <div
                    className="bg-amber-500 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${(data.revenue / maxRevenue) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Booking Status Breakdown + Payment by Service side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-5">
          <h3 className="text-white font-semibold text-sm mb-4">Booking Status</h3>
          {Object.keys(statusCounts).length === 0 ? (
            <p className="text-zinc-600 text-sm text-center py-4">No data</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(statusCounts).map(([st, count]) => (
                <div key={st} className="flex items-center justify-between">
                  <span className={`text-sm capitalize ${
                    st === 'confirmed' ? 'text-blue-400' : st === 'completed' ? 'text-green-400' :
                    st === 'cancelled' ? 'text-red-400' : 'text-amber-400'
                  }`}>{st}</span>
                  <span className="text-white font-semibold">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-5">
          <h3 className="text-white font-semibold text-sm mb-4">Payments by Service</h3>
          {Object.keys(paymentsByService).length === 0 ? (
            <p className="text-zinc-600 text-sm text-center py-4">No data</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(paymentsByService).map(([svc, data]) => (
                <div key={svc} className="flex items-center justify-between">
                  <span className="text-zinc-300 text-sm">{svc}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-zinc-500 text-xs">{data.count} txns</span>
                    <span className="text-green-400 text-sm font-semibold">${data.paid.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hidden print area */}
      <div id="reports-print-area" className="hidden">
        <h1>PDX Window Tint — Revenue Report</h1>
        <div className="meta">Generated: {new Date().toLocaleString()} | Range: {dateRange === 'all' ? 'All Time' : `Last ${dateRange}`}</div>

        <div className="stat-grid">
          <div className="stat-box"><div className="num">${totalCollected.toFixed(2)}</div><div className="label">Collected</div></div>
          <div className="stat-box"><div className="num">${totalPending.toFixed(2)}</div><div className="label">Pending</div></div>
          <div className="stat-box"><div className="num">{filteredBookings.length}</div><div className="label">Bookings</div></div>
          <div className="stat-box"><div className="num">{filteredPayments.length}</div><div className="label">Transactions</div></div>
        </div>

        <h2>Revenue by Service</h2>
        <table>
          <thead><tr><th>Service</th><th>Bookings</th><th>Revenue</th></tr></thead>
          <tbody>
            {Object.entries(revenueByService).map(([svc, data]) => (
              <tr key={svc}><td>{svc}</td><td>{data.count}</td><td>${data.revenue.toFixed(2)}</td></tr>
            ))}
          </tbody>
        </table>

        <h2>Booking Status</h2>
        <table>
          <thead><tr><th>Status</th><th>Count</th></tr></thead>
          <tbody>
            {Object.entries(statusCounts).map(([st, count]) => (
              <tr key={st}><td style={{textTransform:'capitalize'}}>{st}</td><td>{count}</td></tr>
            ))}
          </tbody>
        </table>

        <h2>All Bookings</h2>
        <table>
          <thead><tr><th>Name</th><th>Service</th><th>Package</th><th>Date</th><th>Time</th><th>Status</th><th>Vehicle</th></tr></thead>
          <tbody>
            {filteredBookings.map(b => (
              <tr key={b.id}><td>{b.name}</td><td>{b.service}</td><td>{b.package || ''}</td><td>{b.date}</td><td>{b.time}</td><td style={{textTransform:'capitalize'}}>{b.status}</td><td>{b.vehicle_info || ''}</td></tr>
            ))}
          </tbody>
        </table>

        <h2>All Payments</h2>
        <table>
          <thead><tr><th>Customer</th><th>Service</th><th>Amount</th><th>Status</th><th>Date</th><th>Notes</th></tr></thead>
          <tbody>
            {filteredPayments.map(p => (
              <tr key={p.id}><td>{p.name || ''}</td><td>{p.service || ''}</td><td>${p.amount || 0}</td><td style={{textTransform:'capitalize'}}>{p.payment_status || 'pending'}</td><td>{p.created_at?.slice(0, 10) || ''}</td><td>{p.notes || ''}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const PaymentCard = ({ payment, authHeaders, onRefresh, onOptimisticDelete, selected, onToggleSelect }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({
    name: payment.name || '',
    amount: payment.amount || 0,
    payment_status: payment.payment_status || 'pending',
    notes: payment.notes || '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/payments/${payment.id}`,
        editData,
        { headers: authHeaders }
      );
      toast.success('Payment updated');
      setEditing(false);
      onRefresh();
    } catch {
      toast.error('Failed to update payment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this payment record permanently?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/payments/${payment.id}`, { headers: authHeaders });
      toast.success('Payment deleted');
      if (onOptimisticDelete) onOptimisticDelete(payment.id);
      else onRefresh();
    } catch {
      toast.error('Failed to delete payment');
    }
  };

  const statusOptions = ['pending', 'paid', 'refunded', 'failed'];

  if (editing) {
    return (
      <div data-testid={`payment-card-${payment.id}`} className="bg-zinc-900/40 border border-amber-500/40 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-amber-500 text-xs font-semibold uppercase tracking-wider">Editing Payment</span>
          <button data-testid={`cancel-edit-payment-${payment.id}`} onClick={() => setEditing(false)} className="text-zinc-500 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Customer Name</label>
            <Input
              data-testid={`edit-payment-name-${payment.id}`}
              value={editData.name}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              className="bg-zinc-950 border-zinc-800 text-white h-10"
            />
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Amount ($)</label>
            <Input
              data-testid={`edit-payment-amount-${payment.id}`}
              type="number"
              step="0.01"
              value={editData.amount}
              onChange={(e) => setEditData({ ...editData, amount: parseFloat(e.target.value) || 0 })}
              className="bg-zinc-950 border-zinc-800 text-white h-10"
            />
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Status</label>
            <select
              data-testid={`edit-payment-status-${payment.id}`}
              value={editData.payment_status}
              onChange={(e) => setEditData({ ...editData, payment_status: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 text-white rounded-md h-10 px-3 text-sm"
            >
              {statusOptions.map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1 block">Notes</label>
            <Input
              data-testid={`edit-payment-notes-${payment.id}`}
              value={editData.notes}
              onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              placeholder="Add a note..."
              className="bg-zinc-950 border-zinc-800 text-white h-10"
            />
          </div>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-zinc-800/50">
          <span className="text-zinc-600 text-xs">{payment.service}{payment.package ? ` — ${payment.package}` : ''}</span>
          <div className="flex gap-2">
            <Button
              data-testid={`save-payment-${payment.id}`}
              size="sm"
              disabled={saving}
              onClick={handleSave}
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold text-xs"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
              Save
            </Button>
            <Button
              data-testid={`delete-payment-${payment.id}`}
              size="sm"
              onClick={handleDelete}
              variant="outline"
              className="border-red-800 hover:bg-red-900/40 text-red-400 text-xs"
            >
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`payment-card-${payment.id}`} className={`bg-zinc-900/40 border rounded-xl p-4 transition-all hover:border-zinc-700 ${
      selected ? 'border-amber-500/50 ring-1 ring-amber-500/20' :
      payment.payment_status === 'paid' ? 'border-green-500/30' :
      payment.payment_status === 'refunded' ? 'border-orange-500/30' :
      payment.payment_status === 'failed' ? 'border-red-500/30' :
      'border-zinc-700'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            data-testid={`select-payment-${payment.id}`}
            checked={selected}
            onChange={() => onToggleSelect(payment.id)}
            className="w-4 h-4 rounded border-zinc-700 bg-zinc-950 text-amber-500 focus:ring-amber-500/30 cursor-pointer accent-amber-500"
          />
          <div>
            <span className="text-white font-semibold text-sm">{payment.name || 'Customer'}</span>
            <span className="text-zinc-500 text-xs ml-3">{payment.service}{payment.package ? ` — ${payment.package}` : ''}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-amber-500 font-bold">${payment.amount}</span>
          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${
            payment.payment_status === 'paid' ? 'bg-green-500/15 text-green-400 border border-green-500/30' :
            payment.payment_status === 'pending' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' :
            payment.payment_status === 'refunded' ? 'bg-orange-500/15 text-orange-400 border border-orange-500/30' :
            payment.payment_status === 'failed' ? 'bg-red-500/15 text-red-400 border border-red-500/30' :
            'bg-zinc-700/50 text-zinc-400 border border-zinc-600'
          }`}>
            {payment.payment_status || 'pending'}
          </span>
          <button
            data-testid={`edit-payment-btn-${payment.id}`}
            onClick={() => setEditing(true)}
            className="p-1.5 rounded-lg text-zinc-500 hover:text-amber-500 hover:bg-zinc-800/50 transition-colors"
            title="Edit payment"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="text-zinc-600 text-xs">{payment.created_at?.slice(0, 16).replace('T', ' ')}</div>
        {payment.notes && (
          <div className="flex items-center gap-1 text-zinc-500 text-xs">
            <StickyNote className="w-3 h-3" />
            <span className="truncate max-w-[200px]">{payment.notes}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const FollowUpEmailModal = ({ item, authHeaders, onClose }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody]       = useState('');
  const [generating, setGenerating] = useState(true);
  const [error, setError]     = useState('');
  const [copied, setCopied]   = useState(false);

  useEffect(() => {
    setGenerating(true);
    setError('');

    axios.post(
      `${BACKEND_URL}/api/admin/generate-followup-email`,
      {
        name:    item.name,
        email:   item.email,
        service: item.service,
        status:  item.status,
        amount:  item.amount,
        date:    item.date,
      },
      { headers: authHeaders }
    )
      .then(res => {
        setSubject(res.data.subject || '');
        setBody((res.data.body || '').replace(/\\n/g, '\n'));
      })
      .catch(() => setError('Failed to generate email. Please try again.'))
      .finally(() => setGenerating(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenEmail = () => {
    const mailto = `mailto:${item.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailto, '_blank');
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl overflow-hidden"
        style={{ background: '#111', border: '1px solid #27272a', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #27272a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>Follow-up Email</div>
            <div style={{ color: '#71717a', fontSize: 12, marginTop: 2 }}>To: {item.email}</div>
          </div>
          <button onClick={onClose} style={{ background: '#27272a', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X style={{ width: 16, height: 16, color: '#fff' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {generating && !body && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#71717a', padding: '24px 0' }}>
              <Loader2 style={{ width: 18, height: 18, animation: 'spin 1s linear infinite', color: '#eab308' }} />
              <span style={{ fontSize: 14 }}>Generating personalized email…</span>
            </div>
          )}
          {error && (
            <div style={{ color: '#f87171', fontSize: 13, padding: '12px', background: 'rgba(239,68,68,0.1)', borderRadius: 8 }}>
              Failed to generate email. Please try again.
            </div>
          )}
          {(subject || body) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Subject */}
              <div>
                <label style={{ color: '#71717a', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Subject</label>
                <input
                  data-testid="followup-subject-input"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  style={{ width: '100%', marginTop: 6, padding: '10px 12px', background: '#18181b', border: '1.5px solid #27272a', borderRadius: 10, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontWeight: 600 }}
                  onFocus={e => e.target.style.borderColor = 'rgba(234,179,8,0.5)'}
                  onBlur={e => e.target.style.borderColor = '#27272a'}
                />
              </div>
              {/* Body */}
              <div>
                <label style={{ color: '#71717a', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Message</label>
                <textarea
                  data-testid="followup-body-textarea"
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  rows={10}
                  style={{ width: '100%', marginTop: 6, padding: '10px 12px', background: '#18181b', border: '1.5px solid #27272a', borderRadius: 10, color: '#e4e4e7', fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.6 }}
                  onFocus={e => e.target.style.borderColor = 'rgba(234,179,8,0.5)'}
                  onBlur={e => e.target.style.borderColor = '#27272a'}
                />
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        {(subject || body) && !generating && (
          <div style={{ padding: '12px 20px 16px', borderTop: '1px solid #27272a', display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              data-testid="followup-open-email-btn"
              onClick={handleOpenEmail}
              style={{ flex: 1, padding: '12px', background: '#eab308', border: 'none', borderRadius: 12, color: '#000', fontWeight: 900, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}
            >
              <Mail style={{ width: 15, height: 15 }} /> Open in Email Client
            </button>
            <button
              data-testid="followup-copy-btn"
              onClick={handleCopy}
              style={{ padding: '12px 16px', background: copied ? 'rgba(34,197,94,0.15)' : '#18181b', border: `1px solid ${copied ? 'rgba(34,197,94,0.4)' : '#27272a'}`, borderRadius: 12, color: copied ? '#22c55e' : '#a1a1aa', fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s' }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        )}
      </div>
      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
    </div>
  );
};


const AbandonedCheckoutsSection = ({ authHeaders }) => {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [open, setOpen]         = useState(true);
  const [emailModal, setEmailModal] = useState(null); // { item }

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/abandoned-checkouts`, { headers: authHeaders })
      .then(r => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authHeaders]);

  if (loading) return null;
  if (items.length === 0) return null;

  const statusLabel  = { incomplete: 'Incomplete', failed: 'Failed', cancelled: 'Cancelled' };
  const statusStyle  = {
    incomplete: 'bg-amber-500/15 text-amber-400 border border-amber-500/30',
    failed:     'bg-red-500/15 text-red-400 border border-red-500/30',
    cancelled:  'bg-zinc-500/15 text-zinc-400 border border-zinc-500/30',
  };

  return (
    <>
    <div
      data-testid="abandoned-checkouts-section"
      className="mb-6 rounded-xl overflow-hidden"
      style={{ border: '1.5px solid rgba(234,179,8,0.25)', background: 'rgba(234,179,8,0.04)' }}
    >
      {/* Header */}
      <button
        data-testid="abandoned-checkouts-toggle"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <span className="text-amber-300 font-semibold text-sm">Abandoned Checkouts</span>
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
            {items.length}
          </span>
        </div>
        <div className="flex items-center gap-2 text-zinc-500 text-xs">
          <span className="hidden sm:inline">Started payment but didn't finish</span>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {/* Cards */}
      {open && (
        <div className="px-4 pb-4 grid gap-2.5">
          {items.map(item => (
            <div
              key={item.id}
              data-testid={`abandoned-checkout-${item.id}`}
              className="rounded-lg px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
              style={{ background: '#111', border: '1px solid #27272a', borderLeft: `3px solid ${item.status === 'failed' ? '#ef4444' : '#eab308'}` }}
            >
              {/* Left: customer info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-semibold text-sm">{item.name || 'Unknown customer'}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusStyle[item.status] || statusStyle.incomplete}`}>
                    {statusLabel[item.status] || item.status}
                  </span>
                </div>

                {/* Email — clickable, triggers AI email generation */}
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  {item.email ? (
                    <button
                      data-testid={`abandoned-email-btn-${item.id}`}
                      onClick={() => setEmailModal({ item })}
                      className="flex items-center gap-1.5 text-xs font-medium transition-colors group"
                      style={{ color: '#eab308' }}
                      title="Click to generate follow-up email"
                    >
                      <Mail className="w-3 h-3" />
                      <span className="underline underline-offset-2 decoration-dotted group-hover:decoration-solid">
                        {item.email}
                      </span>
                    </button>
                  ) : item.phone ? (
                    <a href={`tel:${item.phone}`} className="flex items-center gap-1 text-zinc-400 text-xs hover:text-zinc-200 transition-colors">
                      <Phone className="w-3 h-3" />{item.phone}
                    </a>
                  ) : (
                    <span className="text-zinc-600 text-xs italic">No contact info</span>
                  )}
                  {item.service && <span className="text-zinc-500 text-xs">{item.service}</span>}
                  <span className="text-zinc-600 text-xs">
                    {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              </div>

              {/* Right: amount + Stripe link */}
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-white font-bold text-sm">${item.amount?.toFixed(2)}</span>
                {item.stripe_link && (
                  <a
                    href={item.stripe_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`abandoned-stripe-link-${item.id}`}
                    className="p-1.5 rounded-lg text-zinc-500 hover:text-zinc-300 transition-colors"
                    style={{ border: '1px solid #27272a' }}
                    title="View in Stripe"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* AI Email Modal */}
    {emailModal && (
      <FollowUpEmailModal
        item={emailModal.item}
        authHeaders={authHeaders}
        onClose={() => setEmailModal(null)}
      />
    )}
    </>
  );
};


const PaymentsTab = ({ payments, paymentsLoading, fetchPayments, authHeaders, setPayments }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const filtered = statusFilter === 'all' ? payments : payments.filter(p => (p.payment_status || 'pending') === statusFilter);
  const totalRevenue = payments.filter(p => p.payment_status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0);

  const allFilteredSelected = filtered.length > 0 && filtered.every(p => selectedIds.has(p.id));

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filtered.forEach(p => next.delete(p.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filtered.forEach(p => next.add(p.id));
        return next;
      });
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedIds.size;
    if (count === 0) return;
    if (!window.confirm(`Delete ${count} payment record${count > 1 ? 's' : ''} permanently?`)) return;
    setDeleting(true);
    try {
      const idsToDelete = Array.from(selectedIds);
      await axios.post(
        `${BACKEND_URL}/api/admin/payments/bulk-delete`,
        { ids: idsToDelete },
        { headers: authHeaders }
      );
      toast.success(`${count} payment${count > 1 ? 's' : ''} deleted`);
      const idSet = new Set(idsToDelete);
      setPayments(prev => prev.filter(p => !idSet.has(p.id)));
      setSelectedIds(new Set());
    } catch {
      toast.error('Failed to delete payments');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <AbandonedCheckoutsSection authHeaders={authHeaders} />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Payments</h2>
          {payments.length > 0 && (
            <p className="text-zinc-500 text-xs mt-0.5">Total collected: <span className="text-green-400 font-semibold">${totalRevenue.toFixed(2)}</span></p>
          )}
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'paid', 'refunded', 'failed'].map(f => (
            <button
              key={f}
              data-testid={`payment-filter-${f}`}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                statusFilter === f
                  ? 'bg-amber-500 text-black'
                  : 'bg-zinc-900 text-gray-400 hover:text-white'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Select All / Bulk Delete bar */}
      {!paymentsLoading && filtered.length > 0 && (
        <div className="flex items-center justify-between mb-3 px-1">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="select-all-payments"
              checked={allFilteredSelected}
              onChange={toggleSelectAll}
              className="w-4 h-4 rounded border-zinc-700 bg-zinc-950 text-amber-500 focus:ring-amber-500/30 cursor-pointer accent-amber-500"
            />
            <span className="text-zinc-400 text-xs">
              {allFilteredSelected ? 'Deselect all' : 'Select all'}
              {selectedIds.size > 0 && <span className="text-amber-500 ml-1">({selectedIds.size} selected)</span>}
            </span>
          </label>
          {selectedIds.size > 0 && (
            <Button
              data-testid="bulk-delete-payments"
              size="sm"
              disabled={deleting}
              onClick={handleBulkDelete}
              variant="outline"
              className="border-red-800 hover:bg-red-900/40 text-red-400 text-xs"
            >
              {deleting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
              Delete {selectedIds.size} selected
            </Button>
          )}
        </div>
      )}

      {paymentsLoading ? (
        <div className="text-center py-16">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-30" />
          No {statusFilter === 'all' ? '' : statusFilter} transactions found.
        </div>
      ) : (
        <div className="grid gap-3" data-testid="payments-list">
          {filtered.map((p, idx) => (
            <PaymentCard
              key={p.id || idx}
              payment={p}
              authHeaders={authHeaders}
              onRefresh={fetchPayments}
              onOptimisticDelete={(id) => setPayments(prev => prev.filter(x => x.id !== id))}
              selected={selectedIds.has(p.id)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};


const ScheduleTab = ({ authHeaders }) => {
  const [blockedDates, setBlockedDates] = useState([]);
  const [customSlots, setCustomSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [newTime, setNewTime] = useState('');
  const [addingSlot, setAddingSlot] = useState(false);
  // Weekday/Weekend default slots
  const [daySlots, setDaySlots] = useState({ extra_weekday_slots: [], extra_weekend_slots: [], disabled_weekday_slots: [], disabled_weekend_slots: [] });
  const [daySlotInput, setDaySlotInput] = useState({ weekday_extra: '', weekday_disabled: '', weekend_extra: '', weekend_disabled: '' });

  const fetchBlockedDates = useCallback(async () => {
    setLoading(true);
    try {
      const [blockedRes, slotsRes, dayRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/blocked-dates`),
        axios.get(`${BACKEND_URL}/api/custom-time-slots`),
        axios.get(`${BACKEND_URL}/api/admin/day-slots`, { headers: authHeaders }),
      ]);
      setBlockedDates(blockedRes.data);
      setCustomSlots(slotsRes.data);
      setDaySlots(dayRes.data);
    } catch {
      toast.error('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
    try {
      const bookingsRes = await axios.get(`${BACKEND_URL}/api/admin/bookings`, { headers: authHeaders });
      setBookings(bookingsRes.data);
    } catch {}
  }, [authHeaders]);

  useEffect(() => { fetchBlockedDates(); }, [fetchBlockedDates]);

  const addDaySlot = async (scope, action) => {
    const key = `${scope}_${action}`;
    const slot = daySlotInput[key]?.trim();
    if (!slot) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/day-slots`, { scope, action, slot }, { headers: authHeaders });
      setDaySlots(prev => ({ ...prev, [`${action}_${scope}_slots`]: res.data.slots }));
      setDaySlotInput(prev => ({ ...prev, [key]: '' }));
      toast.success(`Added ${slot} to ${scope} ${action} defaults`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add slot'); }
  };

  const removeDaySlot = async (scope, action, slot) => {
    try {
      const res = await axios.delete(`${BACKEND_URL}/api/admin/day-slots`, { data: { scope, action, slot }, headers: authHeaders });
      setDaySlots(prev => ({ ...prev, [`${action}_${scope}_slots`]: res.data.slots }));
      toast.success(`Removed ${slot}`);
    } catch { toast.error('Failed to remove slot'); }
  };

  useEffect(() => { fetchBlockedDates(); }, [fetchBlockedDates]);

  const toggleDate = async (dateStr) => {
    if (selectedDate === dateStr) {
      setSelectedDate(null);
      return;
    }
    const isBlocked = blockedDates.includes(dateStr);
    if (isBlocked) {
      try {
        await axios.delete(`${BACKEND_URL}/api/admin/blocked-dates/${dateStr}`, { headers: authHeaders });
        setBlockedDates(prev => prev.filter(d => d !== dateStr));
        toast.success(`${dateStr} unblocked`);
      } catch {
        toast.error('Failed to unblock date');
      }
    } else {
      setSelectedDate(dateStr);
    }
  };

  const blockDate = async (dateStr) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/blocked-dates`, { date: dateStr }, { headers: authHeaders });
      setBlockedDates(prev => [...prev, dateStr]);
      setSelectedDate(null);
      toast.success(`${dateStr} blocked`);
    } catch {
      toast.error('Failed to block date');
    }
  };

  const addTimeSlot = async () => {
    if (!selectedDate || !newTime) return;
    setAddingSlot(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/custom-time-slots`, { date: selectedDate, time: newTime }, { headers: authHeaders });
      setCustomSlots(prev => [...prev, { date: selectedDate, time: newTime }]);
      setNewTime('');
      toast.success(`Added ${newTime} slot on ${selectedDate}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add time slot');
    } finally {
      setAddingSlot(false);
    }
  };

  const removeTimeSlot = async (date, time) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/custom-time-slots?date=${date}&time=${time}`, { headers: authHeaders });
      setCustomSlots(prev => prev.filter(s => !(s.date === date && s.time === time)));
      toast.success(`Removed ${time} slot from ${date}`);
    } catch {
      toast.error('Failed to remove time slot');
    }
  };

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
  const today = new Date().toISOString().split('T')[0];

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const slotsForDate = (dateStr) => customSlots.filter(s => s.date === dateStr);
  const bookingsForDate = (dateStr) => bookings.filter(b => b.date === dateStr && b.status !== 'cancelled');
  const webBookingsForDate = (dateStr) => bookingsForDate(dateStr).filter(b => b.source !== 'pdxtint_app');
  const appBookingsForDate = (dateStr) => bookingsForDate(dateStr).filter(b => b.source === 'pdxtint_app');

  const timePresets = ['8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM'];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-white">Schedule Management</h2>
          <p className="text-zinc-500 text-xs mt-0.5">Manage default slots and block specific dates</p>
        </div>
      </div>

      {/* Weekday / Weekend Default Slots */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        {[
          { scope: 'weekday', label: 'Weekday Defaults', color: 'blue' },
          { scope: 'weekend', label: 'Weekend Defaults', color: 'amber' },
        ].map(({ scope, label, color }) => (
          <div key={scope} className={`bg-zinc-900/40 border ${color === 'amber' ? 'border-amber-500/20' : 'border-blue-500/20'} rounded-xl p-4`}>
            <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${color === 'amber' ? 'text-amber-400' : 'text-blue-400'}`}>{label}</h3>
            {['extra', 'disabled'].map(action => (
              <div key={action} className="mb-3">
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1.5">{action === 'extra' ? '+ Extra Slots' : '— Disabled Slots'}</p>
                <div className="flex flex-wrap gap-1 mb-1.5 min-h-[24px]">
                  {(daySlots[`${action}_${scope}_slots`] || []).map(slot => (
                    <span key={slot} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${action === 'extra' ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-red-500/15 text-red-400 border border-red-500/30'}`}>
                      {slot}
                      <button onClick={() => removeDaySlot(scope, action, slot)} className="opacity-60 hover:opacity-100 ml-0.5">×</button>
                    </span>
                  ))}
                  {(daySlots[`${action}_${scope}_slots`] || []).length === 0 && <span className="text-zinc-600 text-[10px]">None set</span>}
                </div>
                <div className="flex gap-1">
                  <select value={daySlotInput[`${scope}_${action}`] || ''} onChange={e => setDaySlotInput(prev => ({ ...prev, [`${scope}_${action}`]: e.target.value }))}
                    className="flex-1 text-[10px] bg-zinc-800/60 border border-zinc-700 text-zinc-300 rounded-lg px-2 py-1 focus:outline-none focus:border-amber-500">
                    <option value="">Pick time…</option>
                    {['8:00 AM','9:00 AM','10:00 AM','11:00 AM','12:00 PM','1:00 PM','2:00 PM','3:00 PM','4:00 PM','5:00 PM','6:00 PM','7:00 PM'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <button onClick={() => addDaySlot(scope, action)} className={`px-2 py-1 text-[10px] font-bold rounded-lg ${action === 'extra' ? 'bg-green-600/80 hover:bg-green-500 text-white' : 'bg-red-600/80 hover:bg-red-500 text-white'}`}>+</button>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-white">Per-Day Slots & Blocked Dates</h3>
          <p className="text-zinc-500 text-xs">Click a date to add extra slots or block it</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1.5 text-xs text-zinc-400">
            <span className="w-3 h-3 rounded bg-red-500/30 border border-red-500/50 inline-block" /> Blocked
          </span>
          <span className="flex items-center gap-1.5 text-xs text-zinc-400">
            <span className="w-3 h-3 rounded bg-amber-500/30 border border-amber-500/50 inline-block" /> Has Slots
          </span>
          <span className="flex items-center gap-1.5 text-xs text-zinc-400">
            <span className="w-3 h-3 rounded bg-amber-500/30 border border-amber-500/50 inline-block" /> Selected
          </span>
          <span className="flex items-center gap-1.5 text-xs text-zinc-400">
            <span className="w-2 h-2 rounded-full bg-cyan-400 inline-block" /> Web
          </span>
          <span className="flex items-center gap-1.5 text-xs text-zinc-400">
            <span className="w-2 h-2 rounded-full bg-violet-400 inline-block" /> App
          </span>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Calendar */}
          <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <button onClick={prevMonth} data-testid="schedule-prev-month" className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h3 className="text-white font-semibold text-lg">{monthName}</h3>
              <button onClick={nextMonth} data-testid="schedule-next-month" className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-2 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="text-center text-zinc-500 text-xs font-medium py-1">{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-2">
              {days.map((day, i) => {
                if (!day) return <div key={`empty-${i}`} />;
                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const isBlocked = blockedDates.includes(dateStr);
                const isPast = dateStr < today;
                const isSelected = selectedDate === dateStr;
                const hasSlots = slotsForDate(dateStr).length > 0;
                const webCount = webBookingsForDate(dateStr).length;
                const appCount = appBookingsForDate(dateStr).length;
                const hasBookings = webCount > 0 || appCount > 0;

                return (
                  <button
                    key={dateStr}
                    data-testid={`schedule-date-${dateStr}`}
                    onClick={() => !isPast && toggleDate(dateStr)}
                    onDoubleClick={() => !isPast && !isBlocked && blockDate(dateStr)}
                    disabled={isPast}
                    className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm font-medium transition-all relative ${
                      isPast
                        ? 'text-zinc-700 cursor-not-allowed'
                        : isBlocked
                          ? 'bg-red-500/20 border-2 border-red-500/50 text-red-400 cursor-pointer'
                          : isSelected
                            ? 'bg-amber-500/20 border-2 border-amber-500/50 text-amber-400'
                            : hasBookings
                              ? 'bg-zinc-900/80 border border-zinc-700 text-white hover:bg-zinc-800'
                              : hasSlots
                                ? 'bg-amber-500/10 border border-amber-500/40 text-amber-400 hover:bg-amber-500/20'
                                : dateStr === today
                                  ? 'bg-amber-500/20 border border-amber-500/40 text-amber-400 hover:bg-amber-500/30'
                                  : 'bg-zinc-950/50 border border-zinc-800/50 text-zinc-300 hover:bg-zinc-800 hover:border-zinc-700'
                    }`}
                  >
                    {day}
                    <div className="absolute bottom-0.5 flex gap-0.5">
                      {webCount > 0 && <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />}
                      {appCount > 0 && <span className="w-1.5 h-1.5 rounded-full bg-violet-400" />}
                      {!hasBookings && hasSlots && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                    </div>
                  </button>
                );
              })}
            </div>

            {blockedDates.length > 0 && (
              <div className="mt-6 pt-4 border-t border-zinc-800/50">
                <p className="text-zinc-400 text-xs mb-3 font-medium">Blocked Dates ({blockedDates.length})</p>
                <div className="flex flex-wrap gap-2">
                  {blockedDates.sort().map(d => (
                    <button
                      key={d}
                      data-testid={`unblock-${d}`}
                      onClick={async () => {
                        try {
                          await axios.delete(`${BACKEND_URL}/api/admin/blocked-dates/${d}`, { headers: authHeaders });
                          setBlockedDates(prev => prev.filter(x => x !== d));
                          toast.success(`${d} unblocked`);
                        } catch { toast.error('Failed to unblock'); }
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
                    >
                      <CalendarOff className="w-3 h-3" />
                      {d}
                      <X className="w-3 h-3 ml-1" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Time Slot Manager */}
          <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-6">
            {selectedDate ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-white font-semibold">
                      {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </h3>
                    <p className="text-zinc-500 text-xs mt-0.5">Add available time slots for customers</p>
                  </div>
                  <button
                    onClick={() => blockDate(selectedDate)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/20"
                  >
                    <CalendarOff className="w-3 h-3" />
                    Block Day
                  </button>
                </div>

                {/* Bookings for this date */}
                {bookingsForDate(selectedDate).length > 0 && (
                  <div className="mb-4 p-3 bg-zinc-950/60 rounded-lg border border-zinc-800/50">
                    <p className="text-zinc-400 text-xs font-medium mb-2">
                      Bookings ({bookingsForDate(selectedDate).length})
                    </p>
                    <div className="space-y-2">
                      {bookingsForDate(selectedDate).map(b => (
                        <div key={b.id} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${b.source === 'pdxtint_app' ? 'bg-violet-400' : 'bg-cyan-400'}`} />
                            <span className="text-white font-medium">{b.name}</span>
                            <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase rounded ${
                              b.source === 'pdxtint_app'
                                ? 'bg-violet-500/15 text-violet-400 border border-violet-500/30'
                                : 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                            }`}>
                              {b.source === 'pdxtint_app' ? 'APP' : 'WEB'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-zinc-400">{b.time}</span>
                            <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase rounded-full ${
                              b.status === 'confirmed' ? 'bg-blue-500/15 text-blue-400' :
                              b.status === 'completed' ? 'bg-green-500/15 text-green-400' :
                              'bg-amber-500/15 text-amber-400'
                            }`}>{b.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Existing slots for this date */}
                {slotsForDate(selectedDate).length > 0 && (
                  <div className="mb-4">
                    <p className="text-zinc-400 text-xs font-medium mb-2">Added Slots</p>
                    <div className="flex flex-wrap gap-2">
                      {slotsForDate(selectedDate).sort((a, b) => a.time.localeCompare(b.time)).map(s => (
                        <button
                          key={`${s.date}-${s.time}`}
                          data-testid={`remove-slot-${s.date}-${s.time}`}
                          onClick={() => removeTimeSlot(s.date, s.time)}
                          className="flex items-center gap-1.5 px-3 py-2 text-sm bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-lg hover:bg-amber-500/20 transition-colors"
                        >
                          <Clock className="w-3.5 h-3.5" />
                          {s.time}
                          <X className="w-3 h-3 ml-1" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick-add time presets */}
                <p className="text-zinc-400 text-xs font-medium mb-2">Quick Add</p>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {timePresets.map(t => {
                    const alreadyAdded = slotsForDate(selectedDate).some(s => s.time === t);
                    return (
                      <button
                        key={t}
                        data-testid={`quick-add-${t}`}
                        disabled={alreadyAdded || addingSlot}
                        onClick={async () => {
                          setAddingSlot(true);
                          try {
                            await axios.post(`${BACKEND_URL}/api/admin/custom-time-slots`, { date: selectedDate, time: t }, { headers: authHeaders });
                            setCustomSlots(prev => [...prev, { date: selectedDate, time: t }]);
                            toast.success(`Added ${t}`);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed');
                          } finally {
                            setAddingSlot(false);
                          }
                        }}
                        className={`py-2 px-2 text-xs font-medium rounded-lg transition-all ${
                          alreadyAdded
                            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30 cursor-default'
                            : 'bg-zinc-950/50 border border-zinc-800 text-zinc-400 hover:bg-zinc-800 hover:text-white'
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>

                {/* Custom time input */}
                <p className="text-zinc-400 text-xs font-medium mb-2">Custom Time</p>
                <div className="flex gap-2">
                  <input
                    data-testid="custom-time-input"
                    type="text"
                    placeholder="e.g. 8:30 AM"
                    value={newTime}
                    onChange={e => setNewTime(e.target.value)}
                    className="flex-1 bg-zinc-950/50 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:outline-none"
                  />
                  <button
                    data-testid="add-custom-time-btn"
                    onClick={addTimeSlot}
                    disabled={!newTime || addingSlot}
                    className="px-4 py-2 text-sm font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {addingSlot ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center">
                <CalendarPlus className="w-12 h-12 text-zinc-700 mb-3" />
                <p className="text-zinc-500 text-sm">Select a date on the calendar to add time slots</p>
                <p className="text-zinc-600 text-xs mt-1">Double-click a date to block it entirely</p>
              </div>
            )}

            {/* All custom slots summary */}
            {customSlots.length > 0 && !selectedDate && (
              <div className="mt-4 pt-4 border-t border-zinc-800/50">
                <p className="text-zinc-400 text-xs font-medium mb-3">All Custom Slots ({customSlots.length})</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {Object.entries(customSlots.reduce((acc, s) => {
                    if (!acc[s.date]) acc[s.date] = [];
                    acc[s.date].push(s.time);
                    return acc;
                  }, {})).sort(([a], [b]) => a.localeCompare(b)).map(([date, times]) => (
                    <div key={date} className="flex items-center gap-2 text-xs">
                      <span className="text-zinc-500 w-24 flex-shrink-0">{date}</span>
                      <div className="flex flex-wrap gap-1">
                        {times.sort().map(t => (
                          <span key={`${date}-${t}`} className="px-2 py-0.5 bg-amber-500/10 text-amber-400 rounded text-xs">{t}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


const SettingsTab = ({ authHeaders }) => {
  const [discountActive, setDiscountActive] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(25);
  const [discountServices, setDiscountServices] = useState([]);
  const [depositRequired, setDepositRequired] = useState(true);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [depositToggling, setDepositToggling] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  // Vinyl Wrap Quotes
  const [wrapQuotes, setWrapQuotes] = useState([]);
  const [wrapQuotesOpen, setWrapQuotesOpen] = useState(true);
  const [wrapPrices, setWrapPrices] = useState({});       // { [quoteId]: priceStr }
  const [wrapSending, setWrapSending] = useState({});     // { [quoteId]: bool }

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${BACKEND_URL}/api/settings/discount`),
      axios.get(`${BACKEND_URL}/api/settings/deposit`),
    ]).then(([discRes, depRes]) => {
      setDiscountActive(discRes.data.active);
      setDiscountPercent(discRes.data.percentage || 25);
      setDepositRequired(depRes.data.required);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Fetch vinyl wrap quotes when section is open
  useEffect(() => {
    if (!wrapQuotesOpen) return;
    axios.get(`${BACKEND_URL}/api/admin/vinyl-wrap-quotes`, { headers: authHeaders })
      .then(res => setWrapQuotes(res.data.quotes || []))
      .catch(() => {});
  }, [wrapQuotesOpen]);

  const handleSendWrapPrice = async (quote) => {
    const price = parseFloat(wrapPrices[quote.id]);
    if (!price || price <= 0) { toast.error('Enter a valid price first'); return; }
    setWrapSending(p => ({ ...p, [quote.id]: true }));
    try {
      await axios.post(`${BACKEND_URL}/api/admin/vinyl-wrap-quotes/${quote.id}/send-price`,
        { price }, { headers: authHeaders });
      toast.success(`Quote sent to ${quote.email}!`);
      setWrapQuotes(prev => prev.map(q => q.id === quote.id ? { ...q, status: 'priced', admin_price: price } : q));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send quote');
    } finally { setWrapSending(p => ({ ...p, [quote.id]: false })); }
  };

  const handleToggle = async () => {
    setToggling(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/promo/toggle`, {}, { headers: authHeaders });
      setDiscountActive(res.data.active);
      toast.success(res.data.active ? `Discount enabled — customers see ${res.data.percentage}% OFF` : 'Discount disabled');
    } catch { toast.error('Failed to toggle discount'); } finally { setToggling(false); }
  };

  const handleDepositToggle = async () => {
    setDepositToggling(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/settings/deposit`, {}, { headers: authHeaders });
      setDepositRequired(res.data.required);
      toast.success(res.data.required ? 'Deposit required for all bookings' : 'Deposit disabled — bookings go straight to confirm');
    } catch { toast.error('Failed to toggle deposit'); } finally { setDepositToggling(false); }
  };

  const handlePercentChange = async (pct) => {
    setDiscountPercent(pct);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/promo/percent`, { discount_percent: pct }, { headers: authHeaders });
    } catch { toast.error('Failed to update percentage'); }
  };

  const runAction = async (action, label) => {
    setActionLoading(action);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/actions/${action}`, {}, { headers: authHeaders });
      const count = res.data.marked ?? res.data.restored ?? res.data.deleted ?? 0;
      toast.success(`${label}: ${count} booking(s) affected`);
    } catch { toast.error(`Failed: ${label}`); } finally { setActionLoading(''); }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white">Settings</h2>
        <p className="text-zinc-500 text-xs mt-0.5">Manage promotions and site settings</p>
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto" /></div>
      ) : (
        <div className="space-y-4">
          {/* Discount Card */}
          <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${discountActive ? 'bg-green-500/20' : 'bg-zinc-800/50'}`}>
                  <DollarSign className={`w-5 h-5 ${discountActive ? 'text-green-400' : 'text-zinc-500'}`} />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Discount Promotion</h3>
                  <p className="text-zinc-500 text-xs">{discountActive ? `Active — customers see ${discountPercent}% OFF banner` : 'Disabled'}</p>
                </div>
              </div>
              <button data-testid="toggle-discount-btn" onClick={handleToggle} disabled={toggling} className="flex-shrink-0">
                {toggling ? <Loader2 className="w-9 h-9 text-amber-500 animate-spin" />
                  : discountActive ? <ToggleRight className="w-12 h-12 text-green-400" />
                  : <ToggleLeft className="w-12 h-12 text-zinc-600" />}
              </button>
            </div>
            {/* Percent Picker */}
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-zinc-800/60">
              <span className="text-xs text-zinc-500 self-center mr-1">Discount %:</span>
              {[10, 15, 20, 25, 30, 40, 50].map(pct => (
                <button key={pct} data-testid={`discount-pct-${pct}`}
                  onClick={() => handlePercentChange(pct)}
                  className={`px-3 py-1 text-xs font-bold rounded-full transition-colors ${discountPercent === pct ? 'bg-amber-500 text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}>
                  {pct}%
                </button>
              ))}
            </div>
            {discountActive && (
              <div data-testid="discount-preview" className="mt-3 bg-green-500/10 border border-green-500/30 rounded-xl p-3 text-center">
                <p className="text-green-400 font-bold tracking-wide">{discountPercent}% OFF — Limited Time Only!</p>
                <p className="text-zinc-500 text-xs mt-0.5">What customers see on the booking form</p>
              </div>
            )}
          </div>

          {/* Deposit Card */}
          <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${depositRequired ? 'bg-amber-500/20' : 'bg-zinc-800/50'}`}>
                  <DollarSign className={`w-5 h-5 ${depositRequired ? 'text-amber-400' : 'text-zinc-500'}`} />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Deposit Requirement</h3>
                  <p className="text-zinc-500 text-xs">{depositRequired ? 'Required — customers must pay deposit to confirm' : 'Disabled — bookings confirmed without payment'}</p>
                </div>
              </div>
              <button data-testid="toggle-deposit-btn" onClick={handleDepositToggle} disabled={depositToggling} className="flex-shrink-0">
                {depositToggling ? <Loader2 className="w-9 h-9 text-amber-500 animate-spin" />
                  : depositRequired ? <ToggleRight className="w-12 h-12 text-amber-400" />
                  : <ToggleLeft className="w-12 h-12 text-zinc-600" />}
              </button>
            </div>
          </div>



          {/* Action Cards */}
          <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-5">
            <h3 className="text-white font-semibold text-sm mb-4">Data Actions</h3>
            <div className="space-y-3">
              {[
                { key: 'mark-past-complete', label: 'Mark Past Complete', desc: 'Set all past pending/confirmed bookings → completed', color: 'blue' },
                { key: 'restore-upcoming', label: 'Restore Upcoming', desc: 'Undo misfired mark-past — restore future bookings → confirmed', color: 'amber' },
                { key: 'clear-test-data', label: 'Clear Test Data', desc: 'Delete bookings matching test/demo/taji/verify/ios/edge names', color: 'red' },
              ].map(({ key, label, desc, color }) => (
                <div key={key} className="flex items-center justify-between gap-3 p-3 bg-zinc-800/30 rounded-xl">
                  <div>
                    <p className="text-white text-sm font-medium">{label}</p>
                    <p className="text-zinc-500 text-xs">{desc}</p>
                  </div>
                  <Button data-testid={`action-${key}`} size="sm" disabled={actionLoading === key}
                    onClick={() => runAction(key, label)}
                    className={`flex-shrink-0 text-xs font-bold px-4 ${color === 'red' ? 'bg-red-600 hover:bg-red-500 text-white' : color === 'amber' ? 'bg-amber-500 hover:bg-amber-400 text-black' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}>
                    {actionLoading === key ? <Loader2 className="w-4 h-4 animate-spin" /> : 'RUN'}
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Reviews Fix Card */}
          <div className="bg-zinc-900/40 border border-amber-500/20 rounded-xl p-5">
            <h3 className="text-amber-400 font-semibold text-sm mb-1 flex items-center gap-2">
              <span>Fix Reviews</span>
            </h3>
            <p className="text-zinc-500 text-xs mb-4">Wipes ALL reviews and restores exactly the 14 canonical PDX Window Tint reviews. Also locks the DB so duplicates can never be added again.</p>
            <div className="flex items-center justify-between gap-3 p-3 bg-zinc-800/30 rounded-xl">
              <div>
                <p className="text-white text-sm font-medium">Clean + Restore 14 Reviews</p>
                <p className="text-zinc-500 text-xs">Deletes all duplicates, restores your 14 real reviews</p>
              </div>
              <Button
                data-testid="action-fix-reviews"
                size="sm"
                disabled={actionLoading === 'fix-reviews'}
                onClick={async () => {
                  setActionLoading('fix-reviews');
                  try {
                    const res = await axios.post(`${BACKEND_URL}/api/admin/reviews/fix`, {}, { headers: authHeaders });
                    const { duplicates_removed, total_reviews } = res.data;
                    toast.success(`Done! Cleared ${duplicates_removed.toLocaleString()} records. Your ${total_reviews} reviews are restored.`);
                  } catch { toast.error('Failed to fix reviews'); } finally { setActionLoading(''); }
                }}
                className="flex-shrink-0 text-xs font-bold px-4 bg-amber-500 hover:bg-amber-400 text-black"
              >
                {actionLoading === 'fix-reviews' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'FIX'}
              </Button>
            </div>
          </div>

          {/* ── Vinyl Wrap Quotes Card ── */}
          <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl overflow-hidden">
            {/* Header / Toggle */}
            <div className="flex items-center justify-between p-5 cursor-pointer" onClick={() => setWrapQuotesOpen(o => !o)}>
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-amber-500/15">
                  <DollarSign className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Vinyl Wrap Quotes</h3>
                  <p className="text-zinc-500 text-xs">
                    {wrapQuotes.filter(q => q.status === 'pending').length} pending · review &amp; send personalized prices
                  </p>
                </div>
              </div>
              {wrapQuotesOpen
                ? <ToggleRight className="w-12 h-12 text-amber-400 flex-shrink-0" />
                : <ToggleLeft  className="w-12 h-12 text-zinc-600 flex-shrink-0" />}
            </div>

            {/* Quote list */}
            {wrapQuotesOpen && (
              <div className="border-t border-zinc-800/60 divide-y divide-zinc-800/40">
                {wrapQuotes.length === 0 ? (
                  <p className="text-zinc-600 text-sm text-center py-8">No quote requests yet</p>
                ) : (
                  wrapQuotes.map(q => (
                    <div key={q.id} className="p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-white font-semibold text-sm">{q.name}</p>
                          <p className="text-zinc-500 text-xs">{q.email}{q.phone ? ` · ${q.phone}` : ''}</p>
                          <p className="text-zinc-400 text-xs mt-0.5">
                            {q.coverage} — {q.finish} <span className="font-semibold text-amber-400">{q.color}</span>
                          </p>
                          {q.message && <p className="text-zinc-600 text-xs mt-0.5 italic">"{q.message}"</p>}
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${
                          q.status === 'priced' ? 'bg-green-500/20 text-green-400' :
                          q.status === 'booked' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-amber-500/20 text-amber-400'
                        }`}>
                          {q.status === 'priced' ? `Sent $${q.admin_price}` : q.status}
                        </span>
                      </div>
                      {q.status !== 'booked' && (
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                            <input
                              type="number"
                              placeholder={q.admin_price ? String(q.admin_price) : 'Set price'}
                              value={wrapPrices[q.id] || ''}
                              onChange={e => setWrapPrices(p => ({ ...p, [q.id]: e.target.value }))}
                              className="w-full pl-7 pr-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-xl text-white text-sm focus:outline-none focus:border-amber-500/60"
                              min="0"
                            />
                          </div>
                          <button
                            onClick={() => handleSendWrapPrice(q)}
                            disabled={wrapSending[q.id] || !wrapPrices[q.id]}
                            className="flex items-center gap-1.5 px-4 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold text-sm rounded-xl transition-colors flex-shrink-0"
                          >
                            {wrapSending[q.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};


export default function AdminDashboard() {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [pin, setPin] = useState('');
  const [isLogging, setIsLogging] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filter, setFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const [newBookingIds, setNewBookingIds] = useState(new Set());
  const [unseenCount, setUnseenCount] = useState(0);
  const prevBookingCountRef = useRef(0);
  const isFirstLoadRef = useRef(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [reviewSelectedIds, setReviewSelectedIds] = useState(new Set());
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [discountActive, setDiscountActive] = useState(false);
  const [discountToggling, setDiscountToggling] = useState(false);
  const [depositRequired, setDepositRequired] = useState(true);
  const [depositToggling, setDepositToggling] = useState(false);
  const [showAddBooking, setShowAddBooking] = useState(false);
  const [addingBooking, setAddingBooking] = useState(false);
  const [newBooking, setNewBooking] = useState({
    name: '', email: '', phone: '', service: 'Window Tinting', package: '',
    tint_shade: '', date: '', time: '', vehicle_info: '', message: '',
    payment_method: 'Cash', send_notifications: true
  });

  const authHeaders = { Authorization: `Bearer ${token}` };

  // Fetch discount and deposit status on mount
  useEffect(() => {
    if (token) {
      axios.get(`${BACKEND_URL}/api/settings/discount`).then(res => setDiscountActive(res.data.active)).catch(() => {});
      axios.get(`${BACKEND_URL}/api/settings/deposit`).then(res => setDepositRequired(res.data.required)).catch(() => {});
    }
  }, [token]);

  const handleDiscountToggle = async () => {
    setDiscountToggling(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/settings/discount`, {}, { headers: authHeaders });
      setDiscountActive(res.data.active);
      toast.success(res.data.active ? 'Discount enabled' : 'Discount disabled');
    } catch {
      toast.error('Failed to toggle discount');
    } finally {
      setDiscountToggling(false);
    }
  };

  const handleDepositToggle = async () => {
    setDepositToggling(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/settings/deposit`, {}, { headers: authHeaders });
      setDepositRequired(res.data.required);
      toast.success(res.data.required ? 'Deposit fee enabled' : 'Deposit fee disabled — customers skip payment');
    } catch {
      toast.error('Failed to toggle deposit');
    } finally {
      setDepositToggling(false);
    }
  };

  const fetchData = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/bookings`, { headers: authHeaders }),
        axios.get(`${BACKEND_URL}/api/admin/stats`, { headers: authHeaders }),
      ]);
      const newBookings = bookingsRes.data;
      setStats(statsRes.data);

      if (isFirstLoadRef.current) {
        prevBookingCountRef.current = newBookings.length;
        isFirstLoadRef.current = false;
        setBookings(newBookings);
        return;
      }

      // Detect new bookings
      const prevIds = new Set(bookings.map(b => b.id));
      const freshIds = newBookings.filter(b => !prevIds.has(b.id)).map(b => b.id);

      if (freshIds.length > 0) {
        setNewBookingIds(prev => new Set([...prev, ...freshIds]));
        setUnseenCount(prev => prev + freshIds.length);

        if (soundEnabled) playAlertSound();

        const freshBookings = newBookings.filter(b => freshIds.includes(b.id));
        freshBookings.forEach(b => {
          toast.success(`New booking: ${b.name} — ${b.service}`, {
            icon: <BellRing className="w-5 h-5 text-amber-500" />,
            duration: 8000,
          });

          // Browser push notification
          if (Notification.permission === 'granted') {
            new Notification('PDX Window Tint - New Booking', {
              body: `${b.name} — ${b.service}${b.package ? ` (${b.package})` : ''}\n${b.date} at ${b.time}`,
              icon: '/favicon.ico',
              tag: `booking-${b.id}`,
            });
          }
        });

        // Update page title
        document.title = `(${freshIds.length}) New Booking - PDX Admin`;
        setTimeout(() => { document.title = 'PDX Admin Dashboard'; }, 10000);
      }

      setBookings(newBookings);
      prevBookingCountRef.current = newBookings.length;
    } catch (err) {
      if (err.response?.status === 401) {
        setToken('');
        localStorage.removeItem('admin_token');
        toast.error('Session expired. Please login again.');
      } else if (!silent) {
        toast.error('Failed to load data');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, bookings, soundEnabled]);

  // Initial load
  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  // Polling
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => fetchData(true), POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [token, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLogging(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/login`, { pin: pin.trim() });
      const t = res.data.token;
      setToken(t);
      localStorage.setItem('admin_token', t);
      toast.success('Logged in');
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Invalid PIN');
      } else {
        toast.error('Connection error. Please try again.');
      }
    } finally {
      setIsLogging(false);
    }
  };

  const handleLogout = () => {
    setToken('');
    localStorage.removeItem('admin_token');
    setBookings([]);
    setStats(null);
    document.title = 'PDX Admin Dashboard';
  };

  const handleStatusChange = async (bookingId, status) => {
    setUpdatingId(bookingId);
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/bookings/${bookingId}/status`, { status }, { headers: authHeaders });
      toast.success(`Booking marked as ${status}`);
      fetchData();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdatingId('');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    setUpdatingId(bookingId);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/bookings/${bookingId}`, { headers: authHeaders });
      // Optimistically remove from local state
      setBookings(prev => prev.filter(b => b.id !== bookingId));
      setSelectedIds(prev => { const next = new Set(prev); next.delete(bookingId); return next; });
      toast.success('Booking deleted');
    } catch {
      toast.error('Failed to delete booking');
    } finally {
      setUpdatingId('');
    }
  };

  const toggleSelectBooking = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredBookings.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredBookings.map(b => b.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} booking(s) permanently?`)) return;
    setUpdatingId('bulk');
    const idsToDelete = Array.from(selectedIds);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/bookings/bulk-delete`, { ids: idsToDelete }, { headers: authHeaders });
      setBookings(prev => prev.filter(b => !selectedIds.has(b.id)));
      toast.success(`Deleted ${idsToDelete.length} booking(s)`);
      setSelectedIds(new Set());
    } catch {
      toast.error('Failed to delete selected bookings');
    } finally {
      setUpdatingId('');
    }
  };

  const handleAddBooking = async (e) => {
    e.preventDefault();
    if (!newBooking.name || !newBooking.phone || !newBooking.date || !newBooking.time) {
      toast.error('Name, phone, date and time are required');
      return;
    }
    setAddingBooking(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/bookings`, newBooking, { headers: authHeaders });
      toast.success(`Booking created for ${newBooking.name}`);
      setShowAddBooking(false);
      setNewBooking({
        name: '', email: '', phone: '', service: 'Window Tinting', package: '',
        tint_shade: '', date: '', time: '', vehicle_info: '', message: '',
        payment_method: 'Cash', send_notifications: true
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create booking');
    } finally {
      setAddingBooking(false);
    }
  };

  const clearNewAlerts = async () => {
    setNewBookingIds(new Set());
    setUnseenCount(0);
    document.title = 'PDX Admin Dashboard';
    try {
      await axios.post(`${BACKEND_URL}/api/admin/mark-seen`, {}, { headers: authHeaders });
    } catch {}
  };

  const fetchReviews = useCallback(async () => {
    if (!token) return;
    setReviewsLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/reviews`, { headers: authHeaders });
      setReviews(res.data);
    } catch {
      toast.error('Failed to load reviews');
    } finally {
      setReviewsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token && activeTab === 'reviews') fetchReviews();
  }, [token, activeTab, fetchReviews]);

  const handleApproveReview = async (reviewId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/reviews/${reviewId}/approve`, {}, { headers: authHeaders });
      toast.success('Review approved');
      fetchReviews();
    } catch {
      toast.error('Failed to approve review');
    }
  };

  const handleRejectReview = async (reviewId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/reviews/${reviewId}/reject`, {}, { headers: authHeaders });
      toast.success('Review rejected');
      fetchReviews();
    } catch {
      toast.error('Failed to reject review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/reviews/${reviewId}`, { headers: authHeaders });
      toast.success('Review deleted');
      fetchReviews();
    } catch {
      toast.error('Failed to delete review');
    }
  };

  const filteredReviews = (reviewFilter === 'all' || reviewFilter === 'select')
    ? reviews
    : reviews.filter(r => (r.status || 'pending') === reviewFilter);

  const handleBulkDeleteReviews = async () => {
    if (reviewSelectedIds.size === 0) return;
    if (!window.confirm(`Delete ${reviewSelectedIds.size} review(s) permanently?`)) return;
    const ids = Array.from(reviewSelectedIds);
    let deleted = 0;
    for (const id of ids) {
      try { await axios.delete(`${BACKEND_URL}/api/admin/reviews/${id}`, { headers: authHeaders }); deleted++; } catch {}
    }
    setReviews(prev => prev.filter(r => !reviewSelectedIds.has(r.id)));
    setReviewSelectedIds(new Set());
    setReviewFilter('all');
    toast.success(`Deleted ${deleted} review(s)`);
  };
  const pendingReviewCount = reviews.filter(r => !r.status || r.status === 'pending').length;

  // Gallery management
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryFilter, setGalleryFilter] = useState('all');

  const fetchGallery = useCallback(async () => {
    if (!token) return;
    setGalleryLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/gallery`, { headers: authHeaders });
      setGalleryPhotos(res.data);
    } catch {
      toast.error('Failed to load gallery');
    } finally {
      setGalleryLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token && activeTab === 'gallery') fetchGallery();
  }, [token, activeTab, fetchGallery]);

  const handleApprovePhoto = async (photoId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/gallery/${photoId}/approve`, {}, { headers: authHeaders });
      toast.success('Photo approved');
      fetchGallery();
    } catch {
      toast.error('Failed to approve photo');
    }
  };

  const handleRejectPhoto = async (photoId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/gallery/${photoId}/reject`, {}, { headers: authHeaders });
      toast.success('Photo rejected');
      fetchGallery();
    } catch {
      toast.error('Failed to reject photo');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo permanently?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/gallery/${photoId}`, { headers: authHeaders });
      toast.success('Photo deleted');
      fetchGallery();
    } catch {
      toast.error('Failed to delete photo');
    }
  };

  const pendingGalleryCount = galleryPhotos.filter(p => p.status === 'pending').length;
  const filteredGallery = galleryFilter === 'all' ? galleryPhotos : galleryPhotos.filter(p => p.status === galleryFilter);

  const fetchPayments = useCallback(async () => {
    if (!token) return;
    setPaymentsLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/payments`, { headers: authHeaders });
      setPayments(res.data);
    } catch {
      toast.error('Failed to load payments');
    } finally {
      setPaymentsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token && activeTab === 'payments') fetchPayments();
  }, [token, activeTab, fetchPayments]);

  // Also fetch payments data when viewing reports
  useEffect(() => {
    if (token && activeTab === 'reports') fetchPayments();
  }, [token, activeTab, fetchPayments]);

  const filteredBookings = (() => {
    let result = (filter === 'all' || filter === 'select') ? bookings : bookings.filter(b => b.status === filter);
    if (dateFilter === 'all') return result;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const toISO = (d) => d.toISOString().split('T')[0];
    if (dateFilter === 'today') {
      const s = toISO(today);
      return result.filter(b => b.date === s);
    }
    if (dateFilter === 'this_week') {
      const start = new Date(today); start.setDate(today.getDate() - today.getDay());
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return result.filter(b => b.date >= toISO(start) && b.date <= toISO(end));
    }
    if (dateFilter === 'next_week') {
      const start = new Date(today); start.setDate(today.getDate() - today.getDay() + 7);
      const end = new Date(start); end.setDate(start.getDate() + 6);
      return result.filter(b => b.date >= toISO(start) && b.date <= toISO(end));
    }
    if (dateFilter === 'this_month') {
      const y = today.getFullYear(), m = today.getMonth();
      const start = toISO(new Date(y, m, 1));
      const end = toISO(new Date(y, m + 1, 0));
      return result.filter(b => b.date >= start && b.date <= end);
    }
    if (dateFilter === 'next_month') {
      const y = today.getFullYear(), m = today.getMonth();
      const start = toISO(new Date(y, m + 1, 1));
      const end = toISO(new Date(y, m + 2, 0));
      return result.filter(b => b.date >= start && b.date <= end);
    }
    if (dateFilter === 'custom') {
      return result.filter(b => (!customFrom || b.date >= customFrom) && (!customTo || b.date <= customTo));
    }
    return result;
  })();

  const todayStr = new Date().toISOString().split('T')[0];
  const todayBookings = bookings.filter(b => b.date === todayStr && b.status !== 'cancelled');

  // Login screen
  if (!token) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Toaster position="top-right" />
        <Card className="bg-zinc-900/50 border-zinc-800 w-full max-w-sm">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-white mb-1">PDX Window Tint</h1>
              <p className="text-gray-400 text-sm">Admin Dashboard</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  data-testid="admin-pin-input"
                  type="password"
                  inputMode="numeric"
                  maxLength={10}
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Enter PIN"
                  className="bg-zinc-950 border-zinc-800 text-white text-center text-2xl tracking-[0.5em] h-14"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                data-testid="admin-login-btn"
                disabled={!pin || isLogging}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold h-12"
              >
                {isLogging ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-black">
      <Toaster position="top-right" />
      {/* Header */}
      <header className="bg-zinc-950 border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-white">PDX <span className="text-amber-500">Admin</span></h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="toggle-push-btn"
              onClick={async () => {
                if (notifPermission !== 'granted') {
                  const perm = await Notification.requestPermission();
                  setNotifPermission(perm);
                  if (perm === 'granted') toast.success('Push notifications enabled!');
                  else toast.error('Push notifications blocked');
                } else {
                  toast.info('Push notifications already enabled');
                }
              }}
              className={`p-2 rounded-lg transition-colors ${notifPermission === 'granted' ? 'text-amber-500 hover:bg-zinc-900' : 'text-zinc-600 hover:bg-zinc-900'}`}
              title={notifPermission === 'granted' ? 'Push notifications on' : 'Enable push notifications'}
            >
              {notifPermission === 'granted' ? <Bell className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
            </button>
            <button
              data-testid="toggle-sound-btn"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-2 rounded-lg transition-colors ${soundEnabled ? 'text-amber-500 hover:bg-zinc-900' : 'text-zinc-600 hover:bg-zinc-900'}`}
              title={soundEnabled ? 'Sound on' : 'Sound off'}
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <Button
              data-testid="refresh-btn"
              onClick={() => fetchData()}
              variant="outline"
              size="sm"
              disabled={loading}
              className="border-zinc-800 text-gray-300 hover:bg-zinc-900"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
            </Button>
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-zinc-800 text-gray-300 hover:bg-zinc-900"
            >
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Spacer for bottom nav */}
        <div className="h-2"></div>

        {activeTab === 'bookings' && (
          <div>
            {/* Revenue Summary */}
            {(() => {
              const DEPOSIT_MAP = { 'Window Tinting': 50, 'Vinyl Wrap': 200, 'Starlight Headliner': 200, 'Ambient Lighting': 200, 'Chrome Delete': 100, 'Remove Tint': 100 };
              const today = new Date(); const thisMonth = today.getMonth(); const thisYear = today.getFullYear();
              const todayStr = today.toISOString().split('T')[0];
              const totalDeposits = bookings.reduce((s, b) => s + (DEPOSIT_MAP[b.service] || 50), 0);
              const completed = bookings.filter(b => b.status === 'completed').length;
              const pending = bookings.filter(b => b.status === 'pending').length;
              const thisMonthCount = bookings.filter(b => { const d = new Date(b.date); return d.getMonth() === thisMonth && d.getFullYear() === thisYear; }).length;
              const todayCount = bookings.filter(b => b.date === todayStr).length;
              return (
                <div className="grid grid-cols-3 gap-3 mb-4" data-testid="revenue-summary">
                  <div className="bg-zinc-900/60 border border-amber-500/20 rounded-xl p-3 text-center">
                    <p className="text-amber-400 text-xl font-bold">${totalDeposits.toLocaleString()}</p>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-0.5">Total Deposits</p>
                  </div>
                  <div className="bg-zinc-900/60 border border-blue-500/20 rounded-xl p-3 text-center">
                    <p className="text-blue-400 text-xl font-bold">{bookings.length}</p>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-0.5">All Bookings</p>
                  </div>
                  <div className="bg-zinc-900/60 border border-green-500/20 rounded-xl p-3 text-center">
                    <p className="text-green-400 text-xl font-bold">{completed}</p>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-0.5">Completed</p>
                  </div>
                  <div className="bg-zinc-900/60 border border-zinc-700/40 rounded-xl p-3 text-center">
                    <p className="text-white text-xl font-bold">{thisMonthCount}</p>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-0.5">This Month</p>
                  </div>
                  <div className="bg-zinc-900/60 border border-zinc-700/40 rounded-xl p-3 text-center col-span-2">
                    <p className="text-white text-xl font-bold">{todayCount}</p>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-0.5">Today</p>
                  </div>
                </div>
              );
            })()}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-lg font-semibold text-white">Booking Report</h2>
                <button
                  data-testid="add-booking-btn"
                  onClick={() => setShowAddBooking(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25 transition-colors"
                >
                  <CalendarPlus className="w-3 h-3" />
                  Add Booking
                </button>
                {filter === 'select' && selectedIds.size > 0 && (
                  <button
                    data-testid="bulk-delete-btn"
                    onClick={handleBulkDelete}
                    disabled={updatingId === 'bulk'}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 transition-colors disabled:opacity-50"
                  >
                    {updatingId === 'bulk' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    Delete ({selectedIds.size})
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                {filter === 'select' && filteredBookings.length > 0 && (
                  <label data-testid="select-all-checkbox" className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={filteredBookings.length > 0 && selectedIds.size === filteredBookings.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-amber-500 focus:ring-amber-500/30 cursor-pointer"
                    />
                    <span className="text-xs text-zinc-400">Select All</span>
                  </label>
                )}
                <div className="flex gap-2">
                  {['all', 'select', 'confirmed', 'completed'].map(f => (
                    <button
                      key={f}
                      data-testid={`booking-filter-${f}`}
                      onClick={() => { setFilter(f); setSelectedIds(new Set()); }}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                        filter === f ? 'bg-amber-500 text-black' : 'bg-zinc-900 text-gray-400 hover:text-white'
                      }`}
                    >
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Date Range Filter ── */}
            <div className="flex flex-wrap items-center gap-2 mt-3 pb-3 border-b border-zinc-800/60">
              <span className="text-xs text-zinc-500 mr-1 flex-shrink-0">Appointment Date:</span>
              {[
                { key: 'all', label: 'All' },
                { key: 'today', label: 'Today' },
                { key: 'this_week', label: 'This Week' },
                { key: 'next_week', label: 'Next Week' },
                { key: 'this_month', label: 'This Month' },
                { key: 'next_month', label: 'Next Month' },
                { key: 'custom', label: 'Custom' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  data-testid={`date-filter-${key}`}
                  onClick={() => { setDateFilter(key); setSelectedIds(new Set()); }}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors flex-shrink-0 ${
                    dateFilter === key ? 'bg-blue-500 text-white' : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'
                  }`}
                >
                  {label}
                </button>
              ))}
              {dateFilter === 'custom' && (
                <div className="flex items-center gap-2 ml-1">
                  <input
                    type="date"
                    data-testid="date-filter-from"
                    value={customFrom}
                    onChange={e => { setCustomFrom(e.target.value); setSelectedIds(new Set()); }}
                    className="text-xs bg-zinc-900 border border-zinc-700 text-zinc-300 rounded-lg px-2 py-1 focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-zinc-600 text-xs">to</span>
                  <input
                    type="date"
                    data-testid="date-filter-to"
                    value={customTo}
                    onChange={e => { setCustomTo(e.target.value); setSelectedIds(new Set()); }}
                    className="text-xs bg-zinc-900 border border-zinc-700 text-zinc-300 rounded-lg px-2 py-1 focus:outline-none focus:border-blue-500"
                  />
                </div>
              )}
              {dateFilter !== 'all' && (
                <span className="text-xs text-zinc-500 ml-auto flex-shrink-0">
                  {filteredBookings.length} booking{filteredBookings.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {loading && bookings.length === 0 ? (
              <div className="text-center py-16"><Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto" /></div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                No {filter !== 'all' ? filter : ''} bookings found{dateFilter !== 'all' ? ' for the selected date range' : ''}.
                {dateFilter !== 'all' && <button onClick={() => setDateFilter('all')} className="block mx-auto mt-2 text-amber-400 text-xs underline">Clear date filter</button>}
              </div>
            ) : (
              <div className="grid gap-3" data-testid="bookings-list">
                {filteredBookings.map(b => (
                  <div key={b.id} data-testid={`booking-card-${b.id}`}
                    className={`bg-zinc-900/40 border rounded-xl p-4 ${
                      b.status === 'confirmed' ? 'border-blue-500/30' :
                      b.status === 'completed' ? 'border-green-500/30' :
                      b.status === 'cancelled' ? 'border-red-500/30' :
                      'border-zinc-700'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {filter === 'select' && (
                          <input
                            type="checkbox"
                            data-testid={`select-booking-${b.id}`}
                            checked={selectedIds.has(b.id)}
                            onChange={() => toggleSelectBooking(b.id)}
                            className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-amber-500 focus:ring-amber-500/30 cursor-pointer flex-shrink-0"
                          />
                        )}
                        <span className="text-white font-semibold">{b.name}</span>
                        <span className={`ml-2 px-1.5 py-0.5 text-[9px] font-bold uppercase rounded ${
                          b.source === 'pdxtint_app'
                            ? 'bg-violet-500/15 text-violet-400 border border-violet-500/30'
                            : 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                        }`}>
                          {b.source === 'pdxtint_app' ? 'APP' : 'WEB'}
                        </span>
                        <span className={`ml-2 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${
                          b.status === 'confirmed' ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30' :
                          b.status === 'completed' ? 'bg-green-500/15 text-green-400 border border-green-500/30' :
                          b.status === 'cancelled' ? 'bg-red-500/15 text-red-400 border border-red-500/30' :
                          'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                        }`}>{b.status || 'pending'}</span>
                      </div>
                      <span className="text-zinc-500 text-xs">{b.created_at?.slice(0, 10)}</span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                      <div>
                        <span className="text-zinc-500 text-xs block">Service</span>
                        <span className="text-zinc-300">{b.service}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500 text-xs block">Package</span>
                        <span className="text-zinc-300">{b.package || '—'}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500 text-xs block">Date & Time</span>
                        <span className="text-zinc-300">{b.date} {b.time}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500 text-xs block">Vehicle</span>
                        <span className="text-zinc-300">{b.vehicle_info || '—'}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500 text-xs block">Phone</span>
                        <span className="text-zinc-300">{b.phone}</span>
                      </div>
                      <div className="min-w-0">
                        <span className="text-zinc-500 text-xs block">Email</span>
                        <span className="text-zinc-300 text-xs break-all">{b.email || '—'}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500 text-xs block">Tint Shade</span>
                        <span className="text-zinc-300">{b.tint_shade || '—'}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500 text-xs block">Payment</span>
                        <span className="text-zinc-300">{b.payment_method || '—'}</span>
                      </div>
                    </div>

                    {b.message && (
                      <p className="text-zinc-500 text-xs mb-3 italic">"{b.message}"</p>
                    )}

                    <div className="grid grid-cols-3 gap-2 pt-3 border-t border-zinc-800/50">
                      {b.status === 'pending' ? (
                        <button
                          data-testid={`confirm-booking-${b.id}`}
                          onClick={() => handleStatusChange(b.id, 'confirmed')}
                          disabled={updatingId === b.id}
                          className="flex items-center justify-center gap-1 py-2.5 rounded-lg bg-amber-500 text-black font-bold text-xs hover:bg-amber-400 transition-colors disabled:opacity-50"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {updatingId === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirm'}
                        </button>
                      ) : b.status === 'confirmed' ? (
                        <button
                          data-testid={`complete-booking-${b.id}`}
                          onClick={() => handleStatusChange(b.id, 'completed')}
                          disabled={updatingId === b.id}
                          className="flex items-center justify-center gap-1 py-2.5 rounded-lg bg-green-600 text-white font-bold text-xs hover:bg-green-500 transition-colors disabled:opacity-50"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {updatingId === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Complete'}
                        </button>
                      ) : (
                        <button
                          data-testid={`confirm-booking-${b.id}`}
                          onClick={() => handleStatusChange(b.id, 'confirmed')}
                          disabled={updatingId === b.id}
                          className="flex items-center justify-center gap-1 py-2.5 rounded-lg border border-zinc-700 text-zinc-400 text-xs hover:text-white transition-colors disabled:opacity-50"
                        >
                          {updatingId === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirm'}
                        </button>
                      )}
                      <button
                        data-testid={`delete-booking-${b.id}`}
                        onClick={() => { if (window.confirm('Delete this booking permanently?')) handleDeleteBooking(b.id); }}
                        className="flex items-center justify-center gap-1 py-2.5 rounded-lg bg-red-500/80 text-white font-bold text-xs hover:bg-red-500 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                      <button
                        data-testid={`add-to-calendar-${b.id}`}
                        onClick={() => downloadBookingICS(b)}
                        className="flex items-center justify-center gap-1 py-2.5 rounded-lg border border-blue-500/40 text-blue-400 font-bold text-xs hover:bg-blue-500/10 transition-colors"
                      >
                        <CalendarPlus className="w-3.5 h-3.5" /> Calendar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add Booking Modal */}
        {showAddBooking && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAddBooking(false)}>
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-zinc-800">
                <h3 className="text-lg font-semibold text-white">Add Manual Booking</h3>
                <button data-testid="close-add-booking" onClick={() => setShowAddBooking(false)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleAddBooking} className="p-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Name *</label>
                    <input data-testid="manual-booking-name" value={newBooking.name} onChange={e => setNewBooking(p => ({...p, name: e.target.value}))} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none" required />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Phone *</label>
                    <input data-testid="manual-booking-phone" value={newBooking.phone} onChange={e => setNewBooking(p => ({...p, phone: e.target.value}))} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none" required />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Email</label>
                  <input data-testid="manual-booking-email" type="email" value={newBooking.email} onChange={e => setNewBooking(p => ({...p, email: e.target.value}))} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Service *</label>
                    <select data-testid="manual-booking-service" value={newBooking.service} onChange={e => setNewBooking(p => ({...p, service: e.target.value, package: ''}))} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none">
                      <option>Window Tinting</option>
                      <option>Remove Tint</option>
                      <option>Vinyl Wrap</option>
                      <option>Starlight Headliner</option>
                      <option>Ambient Lighting</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Package</label>
                    <select data-testid="manual-booking-package" value={newBooking.package} onChange={e => setNewBooking(p => ({...p, package: e.target.value}))} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none">
                      <option value="">None</option>
                      {newBooking.service === 'Window Tinting' && <>
                        <option>Front Windshield Only</option>
                        <option>Front Two Windows</option>
                        <option>Sedan & Coupe</option>
                        <option>Trucks & SUV</option>
                      </>}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Date *</label>
                    <input data-testid="manual-booking-date" type="date" value={newBooking.date} onChange={e => setNewBooking(p => ({...p, date: e.target.value}))} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none" required />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Time *</label>
                    <select data-testid="manual-booking-time" value={newBooking.time} onChange={e => setNewBooking(p => ({...p, time: e.target.value}))} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none" required>
                      <option value="">Select time</option>
                      {['9:00 AM','10:00 AM','11:00 AM','12:00 PM','1:00 PM','2:00 PM','3:00 PM','4:00 PM','5:00 PM'].map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Vehicle</label>
                    <input data-testid="manual-booking-vehicle" value={newBooking.vehicle_info} onChange={e => setNewBooking(p => ({...p, vehicle_info: e.target.value}))} placeholder="2024 Toyota Camry" className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none placeholder:text-zinc-600" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 mb-1 block">Payment Method</label>
                    <select data-testid="manual-booking-payment" value={newBooking.payment_method} onChange={e => setNewBooking(p => ({...p, payment_method: e.target.value}))} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none">
                      <option>Cash</option>
                      <option>Card/Apple Pay</option>
                      <option>Zelle</option>
                      <option>Venmo</option>
                      <option>N/A</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Tint Shade</label>
                  <select data-testid="manual-booking-tint" value={newBooking.tint_shade} onChange={e => setNewBooking(p => ({...p, tint_shade: e.target.value}))} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none">
                    <option value="">None</option>
                    <option>5%</option><option>15%</option><option>20%</option><option>35%</option><option>50%</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Notes</label>
                  <textarea data-testid="manual-booking-notes" value={newBooking.message} onChange={e => setNewBooking(p => ({...p, message: e.target.value}))} rows={2} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 outline-none resize-none" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input data-testid="manual-booking-notify" type="checkbox" checked={newBooking.send_notifications} onChange={e => setNewBooking(p => ({...p, send_notifications: e.target.checked}))} className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-amber-500 focus:ring-amber-500/30" />
                  <span className="text-sm text-zinc-300">Send email & SMS notifications</span>
                </label>
                <button data-testid="submit-manual-booking" type="submit" disabled={addingBooking} className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {addingBooking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
                  {addingBooking ? 'Creating...' : 'Create Booking'}
                </button>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-white">Customer Reviews</h2>
                {reviewFilter === 'select' && reviewSelectedIds.size > 0 && (
                  <button data-testid="bulk-delete-reviews-btn" onClick={handleBulkDeleteReviews}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 transition-colors">
                    <Trash2 className="w-3 h-3" /> Delete ({reviewSelectedIds.size})
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                {reviewFilter === 'select' && filteredReviews.length > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input type="checkbox"
                      checked={filteredReviews.length > 0 && reviewSelectedIds.size === filteredReviews.length}
                      onChange={() => {
                        if (reviewSelectedIds.size === filteredReviews.length) setReviewSelectedIds(new Set());
                        else setReviewSelectedIds(new Set(filteredReviews.map(r => r.id)));
                      }}
                      className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-amber-500 cursor-pointer"
                    />
                    <span className="text-xs text-zinc-400">Select All</span>
                  </label>
                )}
                <div className="flex gap-2">
                  {['all', 'approved', 'rejected', 'select'].map(f => (
                    <button key={f} data-testid={`review-filter-${f}`}
                      onClick={() => { setReviewFilter(f); setReviewSelectedIds(new Set()); }}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${reviewFilter === f ? 'bg-amber-500 text-black' : 'bg-zinc-900 text-gray-400 hover:text-white'}`}>
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {reviewsLoading ? (
              <div className="text-center py-16">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto" />
              </div>
            ) : filteredReviews.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
                No {reviewFilter === 'all' ? '' : reviewFilter} reviews found.
              </div>
            ) : (
              <div className="grid gap-4" data-testid="reviews-list">
                {filteredReviews.map(r => (
                  <div key={r.id} data-testid={`review-card-${r.id}`} className={`bg-zinc-900/40 border rounded-xl p-5 transition-all ${
                    (!r.status || r.status === 'pending') ? 'border-amber-500/40' : r.status === 'approved' ? 'border-green-500/30' : 'border-red-500/30'
                  }`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {reviewFilter === 'select' && (
                          <input type="checkbox" checked={reviewSelectedIds.has(r.id)}
                            onChange={() => setReviewSelectedIds(prev => { const s = new Set(prev); s.has(r.id) ? s.delete(r.id) : s.add(r.id); return s; })}
                            className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-amber-500 cursor-pointer flex-shrink-0" />
                        )}
                        <div>
                          <h4 className="text-white font-semibold">{r.name}</h4>
                          <p className="text-amber-500 text-sm">{r.service}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${
                        (r.status || 'pending') === 'pending' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' :
                        r.status === 'approved' ? 'bg-green-500/15 text-green-400 border-green-500/30' :
                        'bg-red-500/15 text-red-400 border-red-500/30'
                      }`}>
                        {(r.status || 'pending').charAt(0).toUpperCase() + (r.status || 'pending').slice(1)}
                      </span>
                    </div>

                    <div className="flex items-center gap-0.5 mb-2">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} className={`w-4 h-4 ${s <= r.rating ? 'text-amber-500 fill-amber-500' : 'text-zinc-700'}`} />
                      ))}
                      <span className="text-zinc-500 text-xs ml-2">{r.rating}/5</span>
                    </div>

                    <p className="text-zinc-300 text-sm mb-3 italic">"{r.review}"</p>

                    <div className="flex items-center justify-between pt-3 border-t border-zinc-800/50">
                      <span className="text-zinc-600 text-xs">{r.created_at?.slice(0, 10)}</span>
                      <div className="flex gap-2">
                        {r.status !== 'approved' && (
                          <button
                            data-testid={`approve-review-${r.id}`}
                            onClick={() => handleApproveReview(r.id)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20 transition-colors"
                          >
                            <ThumbsUp className="w-3 h-3" /> Approve
                          </button>
                        )}
                        {r.status !== 'rejected' && (
                          <button
                            data-testid={`reject-review-${r.id}`}
                            onClick={() => handleRejectReview(r.id)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-colors"
                          >
                            <ThumbsDown className="w-3 h-3" /> Reject
                          </button>
                        )}
                        <button
                          data-testid={`delete-review-${r.id}`}
                          onClick={() => handleDeleteReview(r.id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-900/20 text-red-400 border border-red-800/40 hover:bg-red-900/30 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'reports' && (
          <ReportsTab bookings={bookings} payments={payments} />
        )}

        {activeTab === 'payments' && (
          <PaymentsTab
            payments={payments}
            paymentsLoading={paymentsLoading}
            fetchPayments={fetchPayments}
            authHeaders={authHeaders}
            setPayments={setPayments}
          />
        )}

        {activeTab === 'schedule' && (
          <ScheduleTab authHeaders={authHeaders} />
        )}

        {activeTab === 'settings' && (
          <SettingsTab authHeaders={authHeaders} />
        )}

        {/* Bottom spacer for fixed nav */}
        <div className="h-20"></div>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-lg border-t border-zinc-800 z-50 safe-area-pb" data-testid="bottom-nav">
        <div className="flex justify-around items-center h-16 max-w-lg mx-auto px-2">
          {[
            { id: 'bookings', icon: ClipboardList, label: 'Bookings' },
            { id: 'schedule', icon: CalendarDays, label: 'Schedule' },
            { id: 'reports', icon: BarChart3, label: 'Reports' },
            { id: 'payments', icon: DollarSign, label: 'Payments' },
            { id: 'reviews', icon: Star, label: 'Reviews' },
            { id: 'settings', icon: Settings, label: 'Settings' },
          ].map(tab => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 relative transition-colors ${
                activeTab === tab.id ? 'text-amber-500' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? 'stroke-[2.5]' : ''}`} />
              <span className="text-[10px] font-medium">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="absolute top-0 right-1/4 min-w-[16px] h-[16px] flex items-center justify-center px-1 bg-amber-500 text-black text-[9px] font-bold rounded-full">
                  {tab.badge}
                </span>
              )}
              {activeTab === tab.id && <div className="absolute -top-0 left-1/4 right-1/4 h-0.5 bg-amber-500 rounded-full" />}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
